import * as GraphicsLayer from './GraphicsLayer'
import type { GraphicsLayerHandle, GraphicHandle } from './GraphicsLayer'
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol"
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine'
import { Detail } from '../../parcels/types'
import { ParcelDetailType } from '../../parcels/components/ComplexParcelDetail'


let layerHandle: GraphicsLayerHandle = GraphicsLayer.NULL_LAYER_HANDLE
export type ComplexParcelDetailHighlightHandle = GraphicHandle

let activeHandles : Record<string, Record<ParcelDetailType, Record<string, ComplexParcelDetailHighlightHandle>>> = {}


const selectedSymbol = new SimpleFillSymbol({
  color: [179, 45, 235, .2 ],
  style: "solid",
  outline: { // autocasts as new SimpleLineSymbol()
    color: [ 179, 45, 235, 1 ],
    width: 3
  }
})

export const initComplexParcelDetailsLayer = (map: __esri.Map, view: __esri.MapView) => {
  layerHandle = GraphicsLayer.createGraphicsLayer(map, view)
}

export const getActiveHandle = (PIN: number, type: ParcelDetailType, label: string): ComplexParcelDetailHighlightHandle | undefined => {
  return activeHandles[PIN]?.[type]?.[label]
}

const saveActiveHandle = (label: string, PIN: number, type: ParcelDetailType, handle: ComplexParcelDetailHighlightHandle) => {
  if (!activeHandles[PIN]) activeHandles[PIN] = {} as Record<ParcelDetailType, Record<string, ComplexParcelDetailHighlightHandle>>
  if (!activeHandles[PIN][type]) activeHandles[PIN][type] = {} as Record<string, ComplexParcelDetailHighlightHandle>
  activeHandles[PIN][type][label] = handle
}

const deactivateHandle = (handle: ComplexParcelDetailHighlightHandle) => {
  for (const PIN in activeHandles) {
    for (const type in activeHandles[PIN]) {
      for (const label in activeHandles[PIN][type as ParcelDetailType]) {
        if (activeHandles[PIN][type as ParcelDetailType][label] === handle) {
          delete activeHandles[PIN][type as ParcelDetailType][label]
        }
      }
    }
  }
}

export const addDetailPolygon = (detail: Detail, PIN: number, type: ParcelDetailType): GraphicHandle => {
  const existingHandle = getActiveHandle(PIN, type, detail.label)
  if (existingHandle) return existingHandle
  const handle = GraphicsLayer.createGraphic(layerHandle, { name: detail.label }, detail.geometry, selectedSymbol)
  saveActiveHandle(detail.label, PIN, type, handle)
  GraphicsLayer.addGraphic(layerHandle, handle)
  return handle
}

export const addDetailPolygons = (details: Detail[], PIN: number, type: ParcelDetailType): GraphicHandle => {
  const polygon = geometryEngine.union(details.map(detail => detail.geometry))
  const mergedDetail: Detail = {
    label: details[0].label,
    area: details.reduce((acc, detail) => acc + detail.area, 0),
    geometry: polygon,
  }
  return addDetailPolygon(mergedDetail, PIN, type)
}

export const removeDetailPolygon = (handle: GraphicHandle) => {
  deactivateHandle(handle)
  GraphicsLayer.removeGraphic(layerHandle, handle)
}

export const clearGraphics = () => {
  if (layerHandle === GraphicsLayer.NULL_LAYER_HANDLE) return
  activeHandles = {} as Record<number, Record<ParcelDetailType, Record<string, ComplexParcelDetailHighlightHandle>>>
  GraphicsLayer.clearGraphics(layerHandle)
}